import type { FeatureFlags } from "@/config/features";
import type { TimelineSpan } from "@/data/join-chapters";
import type { JourneyMode } from "@/data/schema/compiled";
import type { TimelineUiPreferences } from "@/lib/timeline/view/TimelineUiPreferences/prefs";
import { CopyLinkButton } from "./CopyLinkButton";
import { SpoilerLevelSelect } from "./SpoilerLevelSelect";
import { TimelineChapterPicker } from "./TimelineChapterPicker";
import { TimelineJourneyToggle } from "./TimelineJourneyToggle";
import { TimelinePhoneSheet } from "./TimelinePhoneSheet";

interface TimelinePlayerSheetProps {
  onClose: () => void;
  onPlaybackHost: (host: HTMLElement | null) => void;
  journey: JourneyMode;
  onJourneyChange: (journey: JourneyMode) => void;
  onJourneyPrefetch: () => void;
  spans: TimelineSpan[];
  spanId: string;
  onSpanIdChange: (id: string) => void;
  uiPrefs: TimelineUiPreferences;
  updateUiPrefs: (patch: Partial<TimelineUiPreferences>) => void;
  featureFlags: FeatureFlags;
  getShareLink: () => string;
}

/** A phone's one place for everything that is not the playback bar. */
export function TimelinePlayerSheet({
  onClose,
  onPlaybackHost,
  journey,
  onJourneyChange,
  onJourneyPrefetch,
  spans,
  spanId,
  onSpanIdChange,
  uiPrefs,
  updateUiPrefs,
  featureFlags,
  getShareLink,
}: TimelinePlayerSheetProps) {
  return (
    <TimelinePhoneSheet onClose={onClose} onPlaybackHost={onPlaybackHost}>
      {featureFlags.universeJourneyView ? (
        <section aria-label="View">
          <h2 className="timeline-sheet__title">View</h2>
          <TimelineJourneyToggle
            journey={journey}
            onJourneyChange={onJourneyChange}
            onJourneyPrefetch={onJourneyPrefetch}
          />
        </section>
      ) : null}
      <section aria-label="Chapters">
        <h2 className="timeline-sheet__title">Chapters</h2>
        <TimelineChapterPicker spans={spans} spanId={spanId} onSpanIdChange={onSpanIdChange} />
      </section>
      <section aria-label="Spoilers and sharing">
        <h2 className="timeline-sheet__title">Spoilers and sharing</h2>
        <SpoilerLevelSelect
          level={uiPrefs.spoilerLevel}
          onChange={(spoilerLevel) => updateUiPrefs({ spoilerLevel })}
        />
        <CopyLinkButton getLink={getShareLink} />
        {featureFlags.boardModelDebugMenu ? (
          <button
            type="button"
            className="timeline-options__debug"
            aria-pressed={uiPrefs.boardModelDebug}
            onClick={() => updateUiPrefs({ boardModelDebug: !uiPrefs.boardModelDebug })}
          >
            Debug
          </button>
        ) : null}
      </section>
    </TimelinePhoneSheet>
  );
}
